"use client";

import * as React from "react";
import { SideOverlayDrawer } from "./side-overlay-drawer";
import { SocialConnectButton, SocialConnectButtonProps } from "./social-connect-button";
import { Button } from "./button";
import { Input } from "./input";
import { DialogDescription, DialogTitle, Modal, ModalBody, ModalFooter, ModalHeader } from "./modal";
import { useTranslations } from "next-intl";
import {
  ACCOUNT_DELETION_CONFIRMATION,
  ACCOUNT_DELETION_REASONS,
  TALK_TO_CEO_CARD_IMAGE_URL,
  FEEDBACK_CARD_IMAGE_URL,
} from "./account-deletion.constants";
import { getCdnAssetUrl } from "@artha/utils";
import type { AccountDeletionRequest, AccountDeletionStep } from "./account-deletion.types";

const enum EditMode {
  NONE = "none",
  PERSONAL_INFO = "personal_info",
}

export interface AccountDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  personalInfo: {
    name: string;
    email: string;
    phone: string;
  };
  onEditPersonalInfo?: () => void;
  onUpdatePersonalInfo?: (data: { name: string; email: string }) => Promise<void>;
  onCheckEmailAvailability?: (email: string) => Promise<{ available: boolean }>;
  onChangePassword?: () => void;
  socialNetworks: SocialConnectButtonProps[];
  onDeleteAccount?: (request: AccountDeletionRequest) => Promise<void>;
  onTalkToCeo?: () => void;
}

export function AccountDrawer({
  isOpen,
  onClose,
  personalInfo,
  onEditPersonalInfo,
  onUpdatePersonalInfo,
  onCheckEmailAvailability,
  onChangePassword,
  socialNetworks,
  onDeleteAccount,
  onTalkToCeo,
}: AccountDrawerProps) {
  const t = useTranslations("accountDrawer");
  const [editMode, setEditMode] = React.useState<EditMode>(EditMode.NONE);
  const [formData, setFormData] = React.useState({
    name: personalInfo.name,
    email: personalInfo.email,
  });
  const [errors, setErrors] = React.useState<{
    name?: string;
    email?: string;
    submit?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = React.useState(false);
  const [emailCheckTimeout, setEmailCheckTimeout] = React.useState<NodeJS.Timeout | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [deleteStep, setDeleteStep] = React.useState<AccountDeletionStep>("overview");
  const [selectedReasons, setSelectedReasons] = React.useState<string[]>([]);
  const [otherReason, setOtherReason] = React.useState("");
  const [deleteConfirmation, setDeleteConfirmation] = React.useState("");
  const [deleteError, setDeleteError] = React.useState("");
  const [isDeleting, setIsDeleting] = React.useState(false);

  // Reset form when drawer opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setFormData({
        name: personalInfo.name,
        email: personalInfo.email,
      });
      setErrors({});
      setIsCheckingEmail(false);
    } else {
      // Reset edit mode when drawer closes
      setEditMode(EditMode.NONE);
    }
  }, [isOpen]);

  // Update form data when personal info changes (but don't reset edit mode)
  React.useEffect(() => {
    if (isOpen && editMode === EditMode.NONE) {
      setFormData({
        name: personalInfo.name,
        email: personalInfo.email,
      });
    }
  }, [personalInfo.name, personalInfo.email, isOpen, editMode]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (emailCheckTimeout) {
        clearTimeout(emailCheckTimeout);
      }
    };
  }, [emailCheckTimeout]);

  const checkEmailAvailability = React.useCallback(async (email: string) => {
    if (!onCheckEmailAvailability || email.toLowerCase() === personalInfo.email.toLowerCase()) {
      return;
    }

    setIsCheckingEmail(true);
    try {
      const result = await onCheckEmailAvailability(email);
      if (!result.available) {
        setErrors(prev => ({ ...prev, email: "Email is already in use" }));
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, email: "Failed to check email availability" }));
    } finally {
      setIsCheckingEmail(false);
    }
  }, [onCheckEmailAvailability, personalInfo.email]);

  const validateForm = async () => {
    const newErrors: typeof errors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length > 100) {
      newErrors.name = "Name must be at most 100 characters";
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = "Please enter a valid email address";
      } else if (formData.email.trim().length > 255) {
        newErrors.email = "Email must be at most 255 characters";
      }
    }

    // If basic validation passes and email changed, check availability
    if (Object.keys(newErrors).length === 0 && formData.email.toLowerCase() !== personalInfo.email.toLowerCase()) {
      if (onCheckEmailAvailability) {
        setIsCheckingEmail(true);
        try {
          const result = await onCheckEmailAvailability(formData.email.trim());
          if (!result.available) {
            newErrors.email = "Email is already in use";
          }
        } catch (error) {
          newErrors.email = "Failed to check email availability";
        } finally {
          setIsCheckingEmail(false);
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEditPersonalInfo = () => {
    setEditMode(EditMode.PERSONAL_INFO);
    // Only call the callback if it exists, but don't depend on it for edit mode
    onEditPersonalInfo?.();
  };

  const handleCancelEdit = () => {
    setEditMode(EditMode.NONE);
    setFormData({
      name: personalInfo.name,
      email: personalInfo.email,
    });
    setErrors({});
  };

  const handleSavePersonalInfo = async () => {
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    if (!onUpdatePersonalInfo) {
      console.error("onUpdatePersonalInfo is not provided");
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      await onUpdatePersonalInfo({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
      });

      setEditMode(EditMode.NONE);
    } catch (error) {
      setErrors({
        submit: error instanceof Error ? error.message : "Failed to update personal information",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }

    // Debounced email availability check
    if (field === "email" && onCheckEmailAvailability) {
      if (emailCheckTimeout) {
        clearTimeout(emailCheckTimeout);
      }

      const timeout = setTimeout(() => {
        const trimmedEmail = value.trim();
        if (trimmedEmail && trimmedEmail.toLowerCase() !== personalInfo.email.toLowerCase()) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (emailRegex.test(trimmedEmail)) {
            checkEmailAvailability(trimmedEmail);
          }
        }
      }, 500);

      setEmailCheckTimeout(timeout);
    }
  };

  const resetDeleteFlow = () => {
    setDeleteStep("overview");
    setSelectedReasons([]);
    setOtherReason("");
    setDeleteConfirmation("");
    setDeleteError("");
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setIsDeleteModalOpen(false);
    resetDeleteFlow();
  };

  const toggleReason = (reason: string) => {
    setSelectedReasons((current) =>
      current.includes(reason) ? current.filter((item) => item !== reason) : [...current, reason]
    );
  };

  const handleDeleteAccount = async () => {
    if (!onDeleteAccount || deleteConfirmation !== ACCOUNT_DELETION_CONFIRMATION) return;

    setIsDeleting(true);
    setDeleteError("");
    try {
      await onDeleteAccount({
        reasons: selectedReasons,
        ...(selectedReasons.includes("other") && otherReason.trim() ? { otherReason: otherReason.trim() } : {}),
      });
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : "Unable to delete your account. Please try again.");
      setIsDeleting(false);
    }
  };

  return (
    <SideOverlayDrawer isOpen={isOpen} onClose={onClose} title="Account" onBack={onClose}>
      <div className="space-y-4">
        <div className="rounded-xl border border-black bg-white p-3">
          <div className="flex items-center justify-between text-sm font-medium text-[#101828]">
            <span>Personal Information</span>
            {editMode === EditMode.PERSONAL_INFO ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  className="text-[#667085]"
                  onClick={handleCancelEdit}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="text-[#0D41E1]"
                  onClick={handleSavePersonalInfo}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </button>
              </div>
            ) : (
              <button type="button" className="text-[#667085]" onClick={handleEditPersonalInfo}>
                Edit
              </button>
            )}
          </div>

          {errors.submit && (
            <div className="mt-2 text-sm text-red-600">
              {errors.submit}
            </div>
          )}

          <div className="mt-4 space-y-3 text-sm text-[#475467]">
            <div>
              <p className="text-xs text-[#667085]">Name</p>
              {editMode === EditMode.PERSONAL_INFO ? (
                <div className="mt-1">
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Enter your name"
                    className="h-[42px]"
                    disabled={isSubmitting}
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                  )}
                </div>
              ) : (
                <div className="mt-1 rounded-lg border border-black px-3 py-2 bg-white">
                  {personalInfo.name}
                </div>
              )}
            </div>

            <div>
              <p className="text-xs text-[#667085]">Email ID</p>
              {editMode === EditMode.PERSONAL_INFO ? (
                <div className="mt-1">
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="Enter your email"
                    className="h-[42px]"
                    disabled={isSubmitting}
                  />
                  {isCheckingEmail && (
                    <p className="mt-1 text-xs text-[#667085]">Checking availability...</p>
                  )}
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                  )}
                </div>
              ) : (
                <div className="mt-1 rounded-lg border border-black px-3 py-2 bg-white">
                  {personalInfo.email}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-black bg-white p-3">
          <div className="text-sm font-medium text-[#101828]">Social media connected</div>
          <div className="mt-3 space-y-2">
            {socialNetworks.map((network) => (
              <SocialConnectButton key={network.label} {...network} />
            ))}
          </div>
        </div>

        {onDeleteAccount && (
          <button
            type="button"
            className="mr-auto block px-1 py-2 text-sm font-medium text-red-600 underline-offset-4 transition-colors hover:text-red-700 hover:underline"
            onClick={() => setIsDeleteModalOpen(true)}
          >
            {t("deleteAccount")}
          </button>
        )}
      </div>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        showCloseButton={!isDeleting}
        className="max-w-xl bg-[#FFF9EF]"
      >
        {deleteStep === "overview" && (
          <>
            <ModalHeader>
              <DialogTitle className="text-xl text-[#101828]">{t("title")}</DialogTitle>
              <DialogDescription className="text-sm leading-6 text-[#667085]">
                {t("description")}
              </DialogDescription>
            </ModalHeader>
            <ModalFooter className="flex-col gap-4">
              <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setDeleteStep("feedback")}
                  className="group relative flex h-44 flex-col justify-between overflow-hidden rounded-3xl border-2 border-black bg-white p-5 text-left shadow-[4px_4px_0px_#000000] transition-all hover:-translate-y-1 hover:shadow-[6px_6px_0px_#000000] active:translate-x-0.5 active:translate-y-0.5 cursor-pointer sm:h-48"
                >
                  <div className="text-left">
                    <span className="text-base font-bold leading-snug text-[#ED5AA9] sm:text-lg">
                      {t("feedbackCardLabel")}
                    </span>
                  </div>
                  <div className="pointer-events-none absolute -bottom-1 -right-2 flex items-end justify-end sm:-bottom-2 sm:-right-2">
                    <img
                      src={FEEDBACK_CARD_IMAGE_URL}
                      alt={t("feedbackCardLabel")}
                      className="h-28 w-auto scale-115 origin-bottom-right object-contain transition-transform duration-200 group-hover:scale-120 sm:h-32"
                      loading="lazy"
                    />
                  </div>
                </button>

                <button
                  type="button"
                  onClick={onTalkToCeo}
                  className="group relative flex h-44 flex-col justify-between overflow-hidden rounded-3xl border-2 border-black bg-white p-5 text-left shadow-[4px_4px_0px_#000000] transition-all hover:-translate-y-1 hover:shadow-[6px_6px_0px_#000000] active:translate-x-0.5 active:translate-y-0.5 cursor-pointer sm:h-48"
                >
                  <div className="text-left">
                    <span className="text-base font-bold leading-snug text-[#ED5AA9] sm:text-lg">
                      {t("talkToCeoCardLabel")}
                    </span>
                  </div>
                  <div className="pointer-events-none absolute -bottom-1 -right-2 flex items-end justify-end sm:-bottom-2 sm:-right-2">
                    <img
                      src={getCdnAssetUrl(TALK_TO_CEO_CARD_IMAGE_URL)}
                      alt={t("talkToCeoCardLabel")}
                      className="h-28 w-auto origin-bottom-right object-contain transition-transform duration-200 group-hover:scale-105 sm:h-32"
                      loading="lazy"
                    />
                  </div>
                </button>
              </div>
              <button type="button" className="text-sm font-medium text-red-600 underline-offset-4 hover:underline" onClick={() => setDeleteStep("confirm")}>
                {t("skipFeedback")}
              </button>
            </ModalFooter>
          </>
        )}

        {deleteStep === "feedback" && (
          <>
            <ModalHeader>
              <DialogTitle className="text-xl text-[#101828]">Tell us what went wrong</DialogTitle>
              <DialogDescription className="text-sm text-[#667085]">Select all that apply. Your feedback helps us improve.</DialogDescription>
            </ModalHeader>
            <ModalBody className="space-y-3 text-left">
              {ACCOUNT_DELETION_REASONS.map((reason) => (
                <label key={reason.value} className="flex cursor-pointer items-start gap-3 rounded-lg border border-[#D0D5DD] p-3 text-sm text-[#344054]">
                  <input
                    type="checkbox"
                    checked={selectedReasons.includes(reason.value)}
                    onChange={() => toggleReason(reason.value)}
                    className="mt-0.5 h-4 w-4 accent-[#0D41E1]"
                  />
                  <span>{reason.label}</span>
                </label>
              ))}
              {selectedReasons.includes("other") && (
                <textarea
                  aria-label="Other reason"
                  value={otherReason}
                  onChange={(event) => setOtherReason(event.target.value)}
                  placeholder="Tell us more"
                  maxLength={500}
                  className="min-h-24 w-full resize-none rounded-lg border border-[#D0D5DD] p-3 text-sm outline-none focus:border-[#0D41E1]"
                />
              )}
            </ModalBody>
            <ModalFooter>
              <div className="grid grid-cols-2 gap-3">
                <Button type="button" variant="outline" className="bg-white" onClick={() => setDeleteStep("overview")}>Back</Button>
                <Button type="button" disabled={selectedReasons.length === 0 || (selectedReasons.includes("other") && !otherReason.trim())} onClick={() => setDeleteStep("confirm")}>
                  Continue
                </Button>
              </div>
            </ModalFooter>
          </>
        )}

        {deleteStep === "confirm" && (
          <>
            <ModalHeader>
              <DialogTitle className="text-xl text-[#101828]">Permanently delete account?</DialogTitle>
              <DialogDescription className="text-sm leading-6 text-[#667085]">
                Type <strong className="text-[#344054]">{ACCOUNT_DELETION_CONFIRMATION}</strong> to confirm.
              </DialogDescription>
            </ModalHeader>
            <ModalBody>
              <Input
                aria-label="Delete account confirmation"
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                placeholder={ACCOUNT_DELETION_CONFIRMATION}
                disabled={isDeleting}
              />
              {deleteError && <p role="alert" className="mt-2 text-left text-sm text-red-600">{deleteError}</p>}
            </ModalBody>
            <ModalFooter>
              <div className="grid grid-cols-2 gap-3">
                <Button type="button" variant="outline" className="bg-white" disabled={isDeleting} onClick={() => setDeleteStep(selectedReasons.length ? "feedback" : "overview")}>Back</Button>
                <Button
                  type="button"
                  className="h-auto min-h-10 bg-red-600 text-white hover:bg-red-700"
                  disabled={deleteConfirmation !== ACCOUNT_DELETION_CONFIRMATION || isDeleting}
                  onClick={handleDeleteAccount}
                >
                  {isDeleting ? "Deleting..." : "Delete permanently"}
                </Button>
              </div>
            </ModalFooter>
          </>
        )}
      </Modal>
    </SideOverlayDrawer>
  );
}
